import type { OpList, ReminderBrief, ReminderView } from "../types";
import { isOperatingList } from "./op-move";

/**
 * Напоминания по строкам оперсписка — общие подписи для колокольчика в шапке,
 * карточки и строки списка. Что сработало, считает сервер
 * (api/src/lib/op-reminders.ts); здесь только слова.
 */

export const MONTHS = [
  "январь", "февраль", "март", "апрель", "май", "июнь",
  "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
];

/** «в марте» — предложный падеж: «дней в марте пока нет». */
const MONTHS_IN = [
  "январе", "феврале", "марте", "апреле", "мае", "июне",
  "июле", "августе", "сентябре", "октябре", "ноябре", "декабре",
];

/** «2027-03-01» → [2027, 2]. */
function ym(iso: string): [number, number] {
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  return m ? [Number(m[1]), Number(m[2]) - 1] : [0, 0];
}

/** «2027-03-01» → «март 2027». */
export function monthLabel(iso: string): string {
  const [y, m] = ym(iso);
  return y ? `${MONTHS[m]} ${y}` : iso;
}

/** «2027-01-15» → «15.01.2027». */
export function ruDate(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso ?? "");
}

type When = Pick<ReminderBrief, "kind" | "want_from" | "want_to" | "remind_on">;

/** «март 2027», «март — апрель 2027», «декабрь 2026 — январь 2027», «15.01.2027». */
export function reminderWhen(r: When): string {
  if (r.kind === "date") return ruDate(r.remind_on);
  if (!r.want_from) return "";
  const [fy, fm] = ym(r.want_from);
  const [ty, tm] = ym(r.want_to ?? r.want_from);
  if (fy === ty && fm === tm) return `${MONTHS[fm]} ${fy}`;
  return fy === ty ? `${MONTHS[fm]} — ${MONTHS[tm]} ${ty}` : `${MONTHS[fm]} ${fy} — ${MONTHS[tm]} ${ty}`;
}

/** «в марте 2027», «в марте — апреле 2027» — для фраз «дней … пока нет». */
export function reminderIn(r: When): string {
  if (r.kind === "date" || !r.want_from) return "";
  const [fy, fm] = ym(r.want_from);
  const [ty, tm] = ym(r.want_to ?? r.want_from);
  if (fy === ty && fm === tm) return `в ${MONTHS_IN[fm]} ${fy}`;
  return fy === ty ? `в ${MONTHS_IN[fm]} — ${MONTHS_IN[tm]} ${ty}` : `в ${MONTHS_IN[fm]} ${fy} — ${MONTHS_IN[tm]} ${ty}`;
}

/** Коротко для строки списка: «март», «март — апрель», «15.01»; год — если не текущий. */
export function reminderShort(r: When): string {
  const year = new Date().getFullYear();
  if (r.kind === "date") {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(r.remind_on ?? "");
    if (!m) return "";
    return Number(m[1]) === year ? `${m[3]}.${m[2]}` : `${m[3]}.${m[2]}.${m[1]}`;
  }
  if (!r.want_from) return "";
  const [fy, fm] = ym(r.want_from);
  const [ty, tm] = ym(r.want_to ?? r.want_from);
  const tail = ty === year ? "" : ` ${ty}`;
  if (fy === ty && fm === tm) return `${MONTHS[fm]}${tail}`;
  return fy === ty ? `${MONTHS[fm]} — ${MONTHS[tm]}${tail}` : `${MONTHS[fm]} ${fy} — ${MONTHS[tm]} ${ty}`;
}

/** «05.03.2027, пятница — ОМС РА» → «05.03, пятница — ОМС РА»: год и так виден в напоминании. */
export function dayShort(label: string): string {
  return label.replace(/^(\d{2}\.\d{2})\.\d{4}/, "$1");
}

/** Почему напоминание сработало — одной строкой. */
export function dueText(v: Pick<ReminderView, "due_reason" | "due_days" | "kind" | "want_from" | "want_to" | "remind_on">): string {
  if (v.due_reason === "date") return `пора: ${ruDate(v.remind_on)}`;
  if (v.due_reason === "passed") {
    const one = !v.want_to || v.want_to.slice(0, 7) === v.want_from?.slice(0, 7);
    return one
      ? `${reminderWhen(v)} прошёл — операционных дней в нём не открывали`
      : `${reminderWhen(v)} прошли — операционных дней в них не открывали`;
  }
  if (v.due_reason === "day") {
    const [first, ...rest] = v.due_days;
    if (!first) return "открыт операционный день";
    return `открыт опердень ${dayShort(first.label)}${rest.length ? ` и ещё ${rest.length}` : ""}`;
  }
  return "";
}

/** Что ждёт напоминание, которое ещё не сработало. */
export function waitText(v: Pick<ReminderView, "kind" | "want_from" | "want_to" | "remind_on" | "booked">): string {
  if (v.kind === "date") return `напомнить ${ruDate(v.remind_on)}`;
  if (v.booked) return `записан(а) на ${dayShort(v.booked.label)} — хотел(а) ${reminderWhen(v)}`;
  return `ждёт опердня: ${reminderWhen(v)}`;
}

/** «день», «дня», «дней» — по числу. */
function plural(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return one;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return few;
  return many;
}

/**
 * Ждущее напоминание в карточке — не повтор «когда», а что будет дальше:
 * «напомним через 9 дней», «дней в марте 2027 пока нет — напомним, как
 * только откроют».
 */
export function cardWaitText(
  v: Pick<ReminderView, "kind" | "want_from" | "want_to" | "remind_on" | "booked">,
  today: string
): string {
  if (v.kind === "date") {
    const [ty, tm, td] = today.split("-").map(Number);
    const [ry, rm, rd] = (v.remind_on ?? today).split("-").map(Number);
    const n = Math.round((Date.UTC(ry, rm - 1, rd) - Date.UTC(ty, tm - 1, td)) / 86_400_000);
    return n <= 1 ? "напомним завтра" : `напомним через ${n} ${plural(n, "день", "дня", "дней")}`;
  }
  const booked = v.booked?.date;
  if (booked && v.want_from && v.want_to && booked >= v.want_from && booked <= v.want_to) {
    return "пациент уже записан на день в эти месяцы — напоминание молчит";
  }
  return `дней ${reminderIn(v)} пока нет — напомним, как только откроют`;
}

/** Операция с имплантацией — ФЭК, имплантация, ИОЛ в названии (как на сервере). */
const isImplantOp = (operType: string) => /фэк|имплант|иол/i.test(operType ?? "");

/**
 * Дни какого списка ждать по умолчанию — туда, куда этого пациента записывают:
 * строка оперирующего списка — его дни; ФЭК и имплантация — дни списка,
 * который списывает линзы; остальное — «Прочие операции».
 */
export function defaultDayList(
  lists: OpList[],
  entry: { list_id: number; oper_type: string }
): OpList | null {
  const operating = lists.filter(isOperatingList);
  const own = operating.find((l) => l.list_id === entry.list_id);
  if (own) return own;
  const implant = isImplantOp(entry.oper_type);
  return (
    operating.find((l) => (implant ? l.writes_off_iol : l.free_oper_type)) ?? operating[0] ?? null
  );
}

/**
 * Напоминание у фамилии в строке списка: колокольчик с месяцем («март») или
 * датой; сработавшее — цветное. Щелчок — карточка напоминания.
 */
export function ReminderChip({ reminder, onOpen }: { reminder: ReminderBrief; onOpen?: () => void }) {
  const title = [
    reminder.due ? "Напоминание сработало" : "Напоминание",
    reminder.kind === "month" ? `хочет оперироваться: ${reminderWhen(reminder)}` : `напомнить ${reminderWhen(reminder)}`,
    reminder.note,
  ]
    .filter(Boolean)
    .join(" — ");
  return (
    <span
      className={`op-rem-chip${reminder.due ? " due" : ""}`}
      title={title}
      onClick={(ev) => {
        ev.stopPropagation();
        onOpen?.();
      }}
    >
      <BellIcon size={11} />
      {reminderShort(reminder)}
    </span>
  );
}

/** Колокольчик — тот же значок в шапке, у строки списка и в меню. */
export function BellIcon({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" className="rem-bell-icon">
      <path
        d="M12 3a6 6 0 0 0-6 6v3.6l-1.6 2.9A1 1 0 0 0 5.3 17h13.4a1 1 0 0 0 .9-1.5L18 12.6V9a6 6 0 0 0-6-6Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinejoin="round"
      />
      <path d="M9.5 19.5a2.6 2.6 0 0 0 5 0" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}
